import { ChevronDownIcon } from "@heroicons/react/outline";
import React from "react";
import { cn } from "ui/lib/utils";
import { ActionMenu } from "./action-menu";
import { useCompactShell } from "./use-compact-shell";

export type ResponsiveSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type ResponsiveSelectProps = {
  /** The sheet's title, and the trigger's name with its current choice. */
  title: string;
  value: string;
  options: ResponsiveSelectOption[];
  onChange: (value: string) => void;
  /** Shown on the trigger when nothing is chosen. */
  placeholder?: string;
  disabled?: boolean;
  triggerClassName?: string;
  /** The native select to render as it is at lg and up; without one, the sheet shows at every width. */
  children?: React.ReactElement;
};

/**
 * A native select on desktop and a picker sheet below lg, where the OS
 * picker breaks the sheet stack and looks like no other choice in the app.
 */
export const ResponsiveSelect = ({
  title,
  value,
  options,
  onChange,
  placeholder = "Choose",
  disabled,
  triggerClassName,
  children,
}: ResponsiveSelectProps) => {
  const compact = useCompactShell();
  if (!compact && children) return children;

  const current = options.find(
    (option) => option.value === String(value ?? "")
  );
  const shown = current?.label ?? placeholder;

  return (
    <ActionMenu
      title={title}
      options={options.map((option) => ({
        value: option.value,
        label: option.label,
        disabled: option.disabled,
        selected: option.value === current?.value,
      }))}
      onSelect={(next) => {
        if (next !== current?.value) onChange(next);
      }}
      trigger={
        <button
          type="button"
          aria-haspopup="dialog"
          aria-label={`${title}: ${shown}`}
          disabled={disabled}
          className={cn(
            "flex min-h-11 w-full min-w-0 items-center justify-between gap-2 rounded-xl border border-white/[0.1] bg-white/[0.06] px-3 text-left text-[15px] text-white/90 transition-colors active:bg-white/[0.1] disabled:opacity-40",
            !current && "text-faint",
            triggerClassName
          )}
        >
          <span className="min-w-0 truncate">{shown}</span>
          <ChevronDownIcon
            aria-hidden="true"
            className="size-4 shrink-0 text-faint"
          />
        </button>
      }
    />
  );
};
